import { Tooltip, Typography, useTunaMediaQuery } from '@tunasong/ui-lib'
import { DragIndicator, MoreVertIcon } from '@tunasong/icons'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { useEditorMessage } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import cn from 'classnames'
import { useState } from 'react'
import type { FC, MouseEvent, SyntheticEvent } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useReadOnly } from 'slate-react'
import styles from './block-gutter.module.css'
import { BlockMenu } from './block-menu.js'
import { BlockToolbar } from './block-toolbar.js'

/**
 * A fine pointer that can hover, on a viewport wide enough to have a margin, gets the drag handle hanging in the
 * margin. Everything else (touch, or < 600px) gets an options button inside the block: there is no margin to hang a
 * handle in, and HTML5 drag does not work on touch.
 */
const pointerDeviceQuery = '(hover: hover) and (pointer: fine) and (min-width: 600px)'

interface BlockGutterProps {
  editor: TunaEditor
  element: CoreElement
  hasFocus: boolean
  isDragging?: boolean
  label?: string
  side?: 'left' | 'right'
  dragRef?: DragElementWrapper<DragSourceOptions>
}

export const BlockGutter: FC<BlockGutterProps> = ({
  editor,
  element,
  hasFocus,
  isDragging = false,
  label,
  side = 'left',
  dragRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [clickedAway, setClickedAway] = useState(0)
  const readOnly = useReadOnly()
  const isPointerDevice = useTunaMediaQuery(pointerDeviceQuery)
  const compact = !isPointerDevice

  const showProperties = useEditorMessage({ editor, type: 'show-block-properties' })
  const handleShowProperties = () => showProperties(element, anchorEl)

  const showComments = useEditorMessage({ editor, type: 'show-comments' })
  const handleShowComments = () => showComments(element, anchorEl)

  const dispatchCreate = useEditorMessage({ editor, type: 'create-anchored-note' })
  const handleAddNote = () => dispatchCreate(element, anchorEl)

  const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (anchorEl || Date.now() < clickedAway + 1000) {
      setAnchorEl(null)
      setClickedAway(0)
      return
    }
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setClickedAway(Date.now())
  }

  const handleMouseDown = (e: SyntheticEvent) => {
    e.stopPropagation()
    // eslint-disable-next-line react-compiler/react-compiler -- Slate requires direct mutation of editor.selection
    editor.selection = null
  }

  const labelContent = label ? (
    <Typography
      variant="body2"
      contentEditable={false}
      className={cn(styles.label, { [styles['label--focused']]: hasFocus })}
    >
      {label}
    </Typography>
  ) : null

  const isRight = side === 'right'

  const labelGutter = labelContent ? (
    <div
      contentEditable={false}
      aria-label={isRight ? 'Gutter right' : 'Gutter left'}
      className={cn(styles.gutter, { [styles['gutter--right']]: isRight })}
    >
      {labelContent}
    </div>
  ) : null

  // In read-only mode, hide the handle but keep the label — section labels are content, not chrome.
  if (readOnly) {
    return labelGutter
  }

  const handleButton = (
    <button
      type="button"
      className={styles.handleButton}
      data-block-id={element.id}
      data-dragging={isDragging ? 'true' : undefined}
      onClick={handleMenu}
      onMouseDown={handleMouseDown}
      aria-label="Block options"
      aria-haspopup="menu"
      aria-expanded={anchorEl !== null}
      contentEditable={false}
      tabIndex={-1}
    >
      {compact ? (
        <MoreVertIcon size={20} className={styles.handleIcon} />
      ) : (
        <DragIndicator size={16} className={styles.handleIcon} />
      )}
    </button>
  )

  return (
    <>
      <BlockToolbar
        tabIndex={-1}
        // On touch the control only opens the menu, so it is not wired as a drag source.
        ref={compact ? undefined : (dragRef as never)}
        data-block-id={element.id}
        data-block-handle={compact ? 'compact' : 'margin'}
        contentEditable={false}
        className={cn(styles.handle, {
          [styles['handle--compact']]: compact,
          [styles['handle--focused']]: hasFocus,
        })}
      >
        {compact ? handleButton : <Tooltip title="Drag to move · Click for options">{handleButton}</Tooltip>}
      </BlockToolbar>
      {labelGutter}
      {anchorEl ? (
        <BlockMenu
          open={true}
          anchorEl={anchorEl}
          block={element}
          onShowProperties={handleShowProperties}
          onComments={handleShowComments}
          onAddNote={handleAddNote}
          onClose={handleCloseMenu}
        />
      ) : null}
    </>
  )
}
